import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Wallet, AlertCircle } from "lucide-react";

/**
 * PaymentSection — POS payments grid: Cash, UPI, Card, Credit, Store Credit, Dues Collection.
 *
 * When store credit is available, it's auto-filled first (up to bill total).
 * When outstanding dues exist, offers option to collect them.
 * Remaining balance is auto-filled in Cash. User can edit any field.
 */
export default function PaymentSection({
  cashAmount,
  setCashAmount,
  upiAmount,
  setUpiAmount,
  cardAmount,
  setCardAmount,
  creditAmount,
  setCreditAmount,
  storeCreditAmount = 0,
  setStoreCreditAmount,
  availableStoreCredit = 0,
  originalStoreCreditInSale = 0, // Original SC used in this sale (for edit mode)
  showStoreCredit = false,
  isEditMode = false,
  // Outstanding dues collection
  outstandingDues = 0,
  duesCollectionAmount = 0,
  setDuesCollectionAmount,
  duesCollectionMode = "cash",
  setDuesCollectionMode,
  billTotal = 0,
}) {
  // Calculate effective max store credit:
  // In edit mode, user can use up to (current available + original SC from this sale)
  // In new sale mode, user can only use up to available credit
  const effectiveMaxCredit = availableStoreCredit + originalStoreCreditInSale;
  
  // Determine if store credit section should be shown:
  // - Customer exists (showStoreCredit=true) AND
  // - Either: has available credit OR is in edit mode with original SC OR current SC amount > 0
  const shouldShowStoreCredit = showStoreCredit && (
    availableStoreCredit > 0 || 
    originalStoreCreditInSale > 0 || 
    storeCreditAmount > 0
  );

  return (
    <div className="rounded-lg border border-[hsl(var(--border))] bg-white shadow-sm p-3" data-testid="sale-payment-section">
      <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Payments</Label>
      <div className="grid grid-cols-2 gap-2 mt-1">
        <div>
          <Label className="text-[10px] text-muted-foreground">Cash</Label>
          <Input
            type="number"
            step="0.01"
            min={0}
            value={cashAmount || ""}
            onChange={(e) => setCashAmount(parseFloat(e.target.value || "0"))}
            className="font-mono-data h-8 text-xs px-2 text-right mt-0.5"
            placeholder=""
            data-testid="sale-pay-cash"
          />
        </div>
        <div>
          <Label className="text-[10px] text-muted-foreground">UPI</Label>
          <Input
            type="number"
            step="0.01"
            min={0}
            value={upiAmount || ""}
            onChange={(e) => setUpiAmount(parseFloat(e.target.value || "0"))}
            className="font-mono-data h-8 text-xs px-2 text-right mt-0.5"
            placeholder=""
            data-testid="sale-pay-upi"
          />
        </div>
        <div>
          <Label className="text-[10px] text-muted-foreground">Card</Label>
          <Input
            type="number"
            step="0.01"
            min={0}
            value={cardAmount || ""}
            onChange={(e) => setCardAmount(parseFloat(e.target.value || "0"))}
            className="font-mono-data h-8 text-xs px-2 text-right mt-0.5"
            placeholder=""
            data-testid="sale-pay-card"
          />
        </div>
        <div>
          <Label className="text-[10px] text-muted-foreground">Credit</Label>
          <Input
            type="number"
            step="0.01"
            min={0}
            value={creditAmount || ""}
            onChange={(e) => setCreditAmount(parseFloat(e.target.value || "0"))}
            className="font-mono-data h-8 text-xs px-2 text-right mt-0.5"
            placeholder=""
            data-testid="sale-pay-credit"
          />
        </div>
      </div>
      
      {/* Store Credit Section - shows when customer has store credit OR editing a sale with store credit */}
      {shouldShowStoreCredit && (
        <div className="mt-3 pt-3 border-t border-dashed">
          <div className="flex items-center gap-2 mb-1.5">
            <Wallet className="w-3.5 h-3.5 text-emerald-600" />
            <Label className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">
              Store Credit {isEditMode && originalStoreCreditInSale > 0 
                ? `(Used in Sale)` 
                : availableStoreCredit > 0 
                  ? `Available: ₹${availableStoreCredit.toFixed(2)}` 
                  : ''}
            </Label>
          </div>
          <Input
            type="number"
            step="0.01"
            min={0}
            max={Math.min(effectiveMaxCredit, billTotal)}
            value={storeCreditAmount || ""}
            onChange={(e) => {
              const val = parseFloat(e.target.value || "0");
              // Cap at effective max credit (available + original in this sale) and bill total
              // Don't use storeCreditAmount in the max calculation (was causing single-digit bug)
              setStoreCreditAmount(Math.min(val, effectiveMaxCredit, billTotal));
            }}
            className="font-mono-data h-8 text-xs px-2 text-right border-emerald-200 focus:border-emerald-400"
            placeholder=""
            data-testid="sale-pay-store-credit"
          />
          <p className="text-[9px] text-muted-foreground mt-1">
            {isEditMode && originalStoreCreditInSale > 0 
              ? `Edit to modify. Effective max: ₹${effectiveMaxCredit.toFixed(2)} (Current: ₹${availableStoreCredit.toFixed(2)} + Original: ₹${originalStoreCreditInSale.toFixed(2)})`
              : 'Auto-filled up to bill total. Edit if needed.'}
          </p>
        </div>
      )}
      
      {/* Outstanding Dues Collection - only shows when customer owes money */}
      {showStoreCredit && outstandingDues > 0 && setDuesCollectionAmount && (
        <div className="mt-3 pt-3 border-t border-dashed">
          <div className="flex items-center gap-2 mb-1.5">
            <AlertCircle className="w-3.5 h-3.5 text-orange-600" />
            <Label className="text-[10px] uppercase tracking-wider text-orange-700 font-semibold">
              Outstanding Dues: ₹{outstandingDues.toFixed(2)}
            </Label>
          </div>
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <Label className="text-[9px] text-muted-foreground">Collect Now</Label>
              <Input
                type="number"
                step="0.01"
                min={0}
                max={outstandingDues}
                value={duesCollectionAmount || ""}
                onChange={(e) => {
                  const val = parseFloat(e.target.value || "0");
                  setDuesCollectionAmount(Math.min(val, outstandingDues));
                }}
                className="font-mono-data h-8 text-xs px-2 text-right border-orange-200 focus:border-orange-400"
                placeholder=""
                data-testid="sale-collect-dues"
              />
            </div>
            {setDuesCollectionMode && (
              <div className="w-20">
                <Label className="text-[9px] text-muted-foreground">Via</Label>
                <select
                  value={duesCollectionMode}
                  onChange={(e) => setDuesCollectionMode(e.target.value)}
                  className="w-full h-8 text-xs px-1 rounded-md border border-orange-200 bg-white"
                  data-testid="sale-dues-mode"
                >
                  <option value="cash">Cash</option>
                  <option value="upi">UPI</option>
                  <option value="card">Card</option>
                </select>
              </div>
            )}
          </div>
          <p className="text-[9px] text-orange-600 mt-1">
            Collected dues will be added to {duesCollectionMode.toUpperCase()} drawer (not to sale total)
          </p>
        </div>
      )}
    </div>
  );
}
